import type { TypesenseProduct } from "@/lib/typesense";
import type { ProductStatus } from "@medusajs/types";
import { getHandlingTimeLabel, SalonStockerHttpTypes } from "@repo/api-sdk";
import { devLogger } from "./devLogger";
import { formatPrice } from "./money";

/**
 * Builds the product title shown in the UI from a product's raw `title` and
 * its `brand` name.
 *
 * Today the product `title` returned by the API already contains the brand
 * name, so we return the title unchanged and deliberately do NOT prepend the
 * brand - doing so duplicated it (e.g. "Wella Wella Color Touch").
 *
 * All product-title rendering that has a separate brand available goes through
 * this single helper so the rule can be changed in one place. If the brand is
 * later stripped from the stored title, flip the body to prepend it, e.g.:
 *
 *   return brand ? `${brand} ${title}` : title;
 */
export function getProductDisplayTitle(input: {
  title: string;
  brand?: string | null;
}): string {
  return input.title;
}

type BaseProduct = Pick<
  TypesenseProduct,
  "id" | "title" | "thumbnail" | "brand" | "categories" | "meta" | "type"
> & {
  status: ProductStatus;
};

interface FormattedProductOverviewData {
  /**
   * The product ID
   */
  productId: string;
  /**
   * Product's status
   */
  status: ProductStatus;
  variantId: string;
  offerId: string;
  title: string;
  brand: string;
  thumbnail: string | null;
  category: string;
  /** The product's type value (e.g. "color_with_variants"), if any. */
  productType?: string;
  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
  sellerName: string;
}

/**
 * The shared min/max -> display math behind a price range: the formatted
 * bounds, plus the saving badge (hidden whenever there's nothing to save, or
 * `max_price` is 0 - avoids a division by zero reading as "100% off").
 * `formattedProductOverviewData` uses this for the product-wide range;
 * `getVariantPriceOverview` uses it for a single variant's own range.
 */
function priceRangeOverview(
  min_price: number,
  max_price: number
): {
  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
} {
  const priceMinFormatted = formatPrice(min_price);
  const priceMaxFormatted = formatPrice(max_price);
  const saving = max_price - min_price;
  const savingInPercentage =
    max_price > 0 ? Math.round((saving / max_price) * 100) : 0;

  return {
    priceMinFormatted,
    priceMaxFormatted: savingInPercentage > 0 ? priceMaxFormatted : undefined,
    priceSavingFormatted:
      savingInPercentage > 0 ? `${savingInPercentage}%` : undefined
  };
}

export function formattedProductOverviewData<T extends BaseProduct>(
  product: T,
  sellersMap: Record<string, SalonStockerHttpTypes.StoreSeller> | undefined
): FormattedProductOverviewData {
  const { categories, meta } = product;

  const { priceMinFormatted, priceMaxFormatted, priceSavingFormatted } =
    priceRangeOverview(meta.min_price, meta.max_price);

  const category = categories.map((c) => c.name).join(", ");
  const seller = sellersMap?.[meta.best_offer.seller_id];

  if (!seller) {
    // this should never happen
    devLogger.log("Unknown seller");
  }

  const data = {
    productId: product.id,
    status: product.status,
    variantId: meta.best_offer.variant_id,
    offerId: meta.best_offer.id,
    title: product.title,
    brand: product.brand.name,
    thumbnail: product.thumbnail,
    category,
    productType: product.type?.value,
    priceMinFormatted,
    priceMaxFormatted,
    priceSavingFormatted,
    sellerName: seller?.name || "Unknown seller"
  } satisfies FormattedProductOverviewData;
  return data;
}

type BaseSellerOffer = SalonStockerHttpTypes.StoreSellerOffer;

interface FormattedSellerOfferData {
  /**
   * Seller's offer ID
   */
  offerId: string;
  variantId: string;
  /**
   * Product's status
   */
  status: SalonStockerHttpTypes.StoreSellerOfferStatus;
  handlingTimeLabel: string;
  priceCalculatedFormatted: string;
  priceOriginalFormatted?: string;
  sellerName: string;
}

export function formattedSellerOfferOverviewData<T extends BaseSellerOffer>(
  offer: T,
  sellersMap: Record<string, SalonStockerHttpTypes.StoreSeller> | undefined
): FormattedSellerOfferData {
  const priceCalculatedFormatted = formatPrice(
    offer.calculated_price.calculated_amount
  );
  const priceOriginalFormatted =
    offer.calculated_price.original_amount >
    offer.calculated_price.calculated_amount
      ? formatPrice(offer.calculated_price.original_amount)
      : undefined;

  const seller = sellersMap?.[offer.seller_id];

  if (!seller) {
    // this should never happen
    devLogger.log("Unknown seller");
  }

  const data = {
    offerId: offer.id,
    variantId: offer.variant_id,
    status: offer.status,
    handlingTimeLabel: getHandlingTimeLabel(offer.handling_time),

    priceCalculatedFormatted,
    priceOriginalFormatted,

    sellerName: seller?.name || "Unknown seller"
  } satisfies FormattedSellerOfferData;
  return data;
}

/**
 * Collapses a product's per-variant offers to the product level: one offer per
 * seller, chosen across ALL variants, so the search card's supplier table
 * reflects the same set of suppliers as the sidebar `sellers[]` facet.
 *
 * Why this exists: the card historically rendered a single variant's
 * `seller_offers` (the `meta.best_offer` variant). A supplier that only stocks a
 * different shade/variant of the product was then absent from the card - even
 * when the buyer had filtered by that very supplier - which read as "the filter
 * returned the wrong product". The filter was correct (it matches the
 * product-level `sellers[]`); the card was under-showing. See
 * salonstocker/storefront#82.
 *
 * "Best per seller" prefers an in-stock (active) offer over a non-active one,
 * then the lowest calculated price. The result is sorted active-first, then by
 * ascending calculated price, so the cheapest buyable supplier leads and the
 * order is deterministic (stable React keys, testable).
 *
 * When `pinnedSellerIds` is passed (the suppliers the buyer has filtered on in
 * the sidebar), those sellers' offers are moved to the front - keeping their
 * relative active-first/price order - so the card leads with the supplier(s) the
 * buyer actually chose rather than the cheapest competitor.
 *
 * Takes just the `seller_offers` shape (not the full `TypesenseProduct`
 * variant) so `getVariantSupplierOffers` below can reuse this - and its
 * active-first/price/pin ordering - for a single variant by wrapping it in a
 * one-variant array.
 */
export function getProductSupplierOffers(
  product: {
    variants: Array<
      Pick<SalonStockerHttpTypes.StoreProductVariantPreview, "seller_offers">
    >;
  },
  pinnedSellerIds?: ReadonlySet<string>
): SalonStockerHttpTypes.StoreSellerOffer[] {
  const isActive = (offer: SalonStockerHttpTypes.StoreSellerOffer) =>
    offer.status === SalonStockerHttpTypes.StoreSellerOfferStatus.ACTIVE;
  const price = (offer: SalonStockerHttpTypes.StoreSellerOffer) =>
    offer.calculated_price.calculated_amount;

  // Between two offers from the SAME seller, keep the more useful one:
  // an in-stock offer beats an out-of-stock one, then cheaper wins.
  const preferred = (
    a: SalonStockerHttpTypes.StoreSellerOffer,
    b: SalonStockerHttpTypes.StoreSellerOffer
  ) => {
    if (isActive(a) !== isActive(b)) return isActive(a) ? a : b;
    return price(a) <= price(b) ? a : b;
  };

  const bestBySeller = new Map<
    string,
    SalonStockerHttpTypes.StoreSellerOffer
  >();
  for (const variant of product.variants) {
    for (const offer of variant.seller_offers) {
      const existing = bestBySeller.get(offer.seller_id);
      bestBySeller.set(
        offer.seller_id,
        existing ? preferred(existing, offer) : offer
      );
    }
  }

  const sorted = [...bestBySeller.values()].sort((a, b) => {
    if (isActive(a) !== isActive(b)) return isActive(a) ? -1 : 1;
    return price(a) - price(b);
  });

  if (!pinnedSellerIds || pinnedSellerIds.size === 0) return sorted;

  const pinned = sorted.filter((o) => pinnedSellerIds.has(o.seller_id));
  const rest = sorted.filter((o) => !pinnedSellerIds.has(o.seller_id));
  return [...pinned, ...rest];
}

/**
 * A single variant's own supplier offers - active-first, then cheapest,
 * pinned suppliers moved to the front - the scoped counterpart to
 * `getProductSupplierOffers` used once the buyer has narrowed a search-result
 * card to one variant (e.g. a shade) instead of the product-wide aggregate.
 */
export function getVariantSupplierOffers(
  variant: Pick<
    SalonStockerHttpTypes.StoreProductVariantPreview,
    "seller_offers"
  >,
  pinnedSellerIds?: ReadonlySet<string>
): SalonStockerHttpTypes.StoreSellerOffer[] {
  return getProductSupplierOffers({ variants: [variant] }, pinnedSellerIds);
}

/**
 * A single variant's own price range across the sellers currently offering
 * it - the scoped counterpart to `formattedProductOverviewData`'s
 * product-wide min/max, used for the same narrowed-to-one-variant card state
 * as `getVariantSupplierOffers`.
 */
export function getVariantPriceOverview(
  variant: Pick<
    SalonStockerHttpTypes.StoreProductVariantPreview,
    "seller_offers"
  >
): {
  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
} {
  const prices = variant.seller_offers.map(
    (offer) => offer.calculated_price.calculated_amount
  );
  if (prices.length === 0) {
    return { priceMinFormatted: formatPrice(0) };
  }
  return priceRangeOverview(Math.min(...prices), Math.max(...prices));
}

/** A variant shape narrow enough to match against a free-text search query. */
type MatchableVariant = Pick<
  SalonStockerHttpTypes.StoreProductVariantPreview,
  "id" | "title" | "options"
>;

/**
 * Splits free text into lowercase word tokens for keyword matching. Keeps
 * internal punctuation (so shade codes like "6.1" or "10.21" stay one token)
 * and only splits on whitespace and common list/sentence separators; a
 * leading/trailing dash (from " - " title separators, e.g. "5G - Copper
 * Gold") is trimmed off each token rather than splitting on it.
 */
function tokenize(input: string): string[] {
  return input
    .toLowerCase()
    .split(/[\s,;:/()]+/)
    .map((token) => token.replace(/^[-–—]+|[-–—]+$/g, ""))
    .filter(Boolean);
}

/**
 * Finds the variant a free-text search query names precisely enough to
 * auto-select - e.g. searching "redken eq gloss 5g" should land on the "5G"
 * variant. A variant only matches when EVERY token of its title (or one of
 * its option values, such as a shade code) appears in the query; among
 * variants that qualify, the one whose matching candidate has the most
 * tokens wins (the more specific match). Single-character candidates (e.g. a
 * bare "N" tone code) are ignored - too likely to collide with an unrelated
 * word in the query.
 *
 * Returns `undefined` when nothing in the query names a variant precisely
 * enough (including when the product has only one variant, so there's
 * nothing to disambiguate), so callers fall back to the product's default
 * variant. Used by `SearchResults` to seed
 * `ProductCardWithPriceComparison`'s variant selector.
 */
export function findVariantMatchingSearchQuery<T extends MatchableVariant>(
  variants: T[],
  query: string | null | undefined
): T | undefined {
  if (!query || variants.length <= 1) return undefined;

  const queryTokens = new Set(tokenize(query));
  if (queryTokens.size === 0) return undefined;

  let best: T | undefined;
  let bestScore = 0;

  for (const variant of variants) {
    // A `Set` so a variant whose title already equals its sole option value
    // (the common case - e.g. title "5G", option value "5G") is tokenized
    // once instead of twice.
    const candidates = new Set([
      variant.title,
      ...variant.options.map((option) => option.value)
    ]);

    for (const candidate of candidates) {
      const candidateTokens = tokenize(candidate);
      const isTooVague =
        candidateTokens.length === 0 ||
        (candidateTokens.length === 1 && candidateTokens[0].length < 2);
      if (isTooVague) continue;

      const isFullMatch = candidateTokens.every((token) =>
        queryTokens.has(token)
      );
      if (isFullMatch && candidateTokens.length > bestScore) {
        bestScore = candidateTokens.length;
        best = variant;
      }
    }
  }

  return best;
}
